// src/shared/composables/useDomainsManager.ts

import { AsyncHandlerOptions, createError, useAsyncHandler } from '@/shared/composables/useAsyncHandler';
import { useDomainContext } from '@/shared/composables/useDomainContext';
import { ApplicationError } from '@/schemas/errors';
import type { CustomDomain } from '@/schemas/shapes/v3';
import { useDomainsStore, useNotificationsStore } from '@/shared/stores';
import { storeToRefs } from 'pinia';
import { computed, onScopeDispose, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

/**
 * Composable for managing custom domains and their brand settings
 *
 * useDomainsManager's role should be:
 * - Managing UI-specific state (loading states, toggling states)
 * - Coordinating between stores and UI components
 * - Handling user interactions and confirmations
 * - Providing a simplified interface for common domain operations
 */
/* eslint-disable max-lines-per-function */
export function useDomainsManager() {
  const store = useDomainsStore();
  const notifications = useNotificationsStore();
  const router = useRouter();
  const route = useRoute();
  const goBack = () => router.back();
  const { records, details } = storeToRefs(store);

  // Track pending timeouts so they can be cleared on scope disposal
  let redirectTimer: ReturnType<typeof setTimeout> | null = null;
  let verifyTimer: ReturnType<typeof setTimeout> | null = null;
  onScopeDispose(() => {
    if (redirectTimer) {
      clearTimeout(redirectTimer);
      redirectTimer = null;
    }
    if (verifyTimer) {
      clearTimeout(verifyTimer);
      verifyTimer = null;
    }
  });

  // Get org identifier from route params for org-qualified operations
  // Routes use either :orgid (domain routes) or :extid (org settings routes)
  const orgIdentifier = computed(() =>
    (route.params.orgid || route.params.extid) as string | undefined
  );

  // Legacy alias for backward compatibility with navigation code
  const orgid = orgIdentifier;
  const { t } = useI18n();

  const recordCount = computed(() => store.recordCount());

  // Local state
  const isLoading = ref(false);
  const error = ref<ApplicationError | null>(null); // Add local error state

  const defaultAsyncHandlerOptions: AsyncHandlerOptions = {
    notify: (message, severity) => {
      notifications.show(message, severity, 'top');
    },
    setLoading: (loading) => (isLoading.value = loading),
    onError: (err) => {
      // 404: resource not found - redirect to NotFound page
      if (err.code === 404) {
        return router.push({ name: 'NotFound' });
      }

      // Set local error state for inline ErrorDisplay.
      // Notification is handled by wrap()'s notify handler — don't duplicate it here.
      error.value = err;
    },
  };

  // Composable async handler
  const { wrap } = useAsyncHandler(defaultAsyncHandlerOptions);

  /**
   * Fetch domains list
   * @param force - Force refresh even if already initialized
   */
  const fetch = async () => wrap(async () => await store.fetchList());

  const getDomain = async (extid: string) =>
    wrap(async () => {
      const domainData = await store.getDomain(extid);
      const domain = domainData.record;
      const currentTime = Math.floor(Date.now() / 1000);
      const lastMonitored = domain?.vhost?.last_monitored_unix ?? 0;
      const domainUpdated = Math.floor(domain.updated.getTime() / 1000);
      const lastUpdatedDistance = currentTime - domainUpdated;
      // Approximated hasn't checked this domain yet or it's been more than N
      // seconds since this domain record was updated. Typically this will be
      // the amount of time since last clicking the "refresh" button.
      const canVerify = !lastMonitored || lastUpdatedDistance >= 10;

      return {
        domain,
        cluster: (domainData?.details as any)?.cluster,
        canVerify,
      };
    });

  const verifyDomain = async (extid: string) =>
    wrap(async () => {
      const result = await store.verifyDomain(extid);
      notifications.show(t('web.domains.domain_verification_initiated_successfully'), 'success', 'top');
      return result;
    });

  const handleDomainExistsError = async (domain: string, errorMessage: string) => {
    if (errorMessage.includes('already registered in your organization')) {
      notifications.show(t('web.domains.domain_already_in_organization'), 'warning', 'top');
      // Best-effort: refresh and redirect to the existing domain's verify page.
      // If fetchList fails (e.g. schema mismatch), we still show the warning above.
      try {
        await store.fetchList();
        const existingDomain = store.records?.find(d => d.display_domain === domain);
        if (existingDomain && orgid.value) {
          redirectTimer = setTimeout(() => {
            redirectTimer = null;
            router.push({
              name: 'DomainVerify',
              params: { orgid: orgid.value, extid: existingDomain.extid },
            });
          }, 2000);
        }
      } catch {
        // fetchList failure shouldn't mask the duplicate-domain warning
      }
      return null;
    }
    if (errorMessage.includes('registered to another organization')) {
      notifications.show(t('web.domains.domain_in_other_organization'), 'error', 'top');
      return null;
    }
    return undefined; // Signal to re-throw
  };

  /** Update domain context after adding a domain */
  const updateDomainContextAfterAdd = (
    record: CustomDomain,
    details: { domain_context?: string | null } | undefined
  ) => {
    const { setContext } = useDomainContext();
    const contextFromServer = details?.domain_context;
    // Skip backend sync when server already set the context
    if (contextFromServer) {
      setContext(contextFromServer, true);
    } else {
      setContext(record.display_domain);
    }
  };

  const handleAddDomain = async (domain: string) =>
    wrap(async () => {
      try {
        // Pass org_id from route params to ensure correct org context
        const result = await store.addDomain(domain, orgid.value);
        if (!result?.record) {
          error.value = createError(t('web.domains.failed_to_add_domain'), 'human', 'error');
          return null;
        }

        const { record, details } = result;
        const isReclaimed = record.updated.getTime() > record.created.getTime();
        const message = isReclaimed ? 'web.domains.domain_claimed_successfully' : 'web.domains.domain_added_successfully';
        notifications.show(t(message), 'success', 'top');

        updateDomainContextAfterAdd(record, details);

        if (orgid.value) {
          router.push({
            name: 'DomainVerify',
            params: { orgid: orgid.value, extid: record.extid },
          });
        }
        verifyTimer = setTimeout(() => {
          verifyDomain(record.extid).catch((err: unknown) => {
            console.warn('[useDomainsManager] Post-add verification failed:', err);
          });
        }, 2000);
        return record;
      } catch (err: unknown) {
        const axiosErr = err as { response?: { data?: { error?: string } }; message?: string };
        const errorMessage = axiosErr?.response?.data?.error || axiosErr?.message || '';
        const handled = await handleDomainExistsError(domain, errorMessage);
        if (handled !== undefined) return handled;
        throw err;
      }
    });

  const deleteDomain = async (domainId: string) =>
    wrap(async () => {
      await store.deleteDomain(domainId);
      notifications.show(t('web.domains.domain_removed_successfully'), 'success', 'top');
    });

  const pendingOrgRole = ref<string | null>(null);

  const { wrap: wrapEntitlementAware } = useAsyncHandler({
    ...defaultAsyncHandlerOptions,
    notify: false,
    onError: (err: ApplicationError) => {
      error.value = err;
      if (err.details?.error_type === 'EntitlementRequired') {
        const key = pendingOrgRole.value === 'owner'
          ? 'web.domains.entitlement_required_owner'
          : 'web.domains.entitlement_required_member';
        notifications.show(t(key), 'error', 'top');
      } else {
        notifications.show(err.message, err.severity, 'top');
        defaultAsyncHandlerOptions.onError?.(err);
      }
    },
  });

  const toggleHomepageConfig = async (extid: string, enabled: boolean, orgRole?: string | null) => {
    pendingOrgRole.value = orgRole ?? null;
    return wrapEntitlementAware(async () => await store.putHomepageConfig(extid, enabled));
  };

  /**
   * Refresh domain records for the current organization context.
   * Uses org identifier from route params (:orgid or :extid) to ensure
   * correct org-scoped fetch, especially after org creation/switch.
   */
  const refreshRecords = async (force = false) => store.refreshRecords({ orgId: orgIdentifier.value, force });

  return {
    // State
    records,
    details,
    isLoading,
    error,

    // Getters
    recordCount,

    // Actions
    fetch,
    getDomain,
    verifyDomain,
    handleAddDomain,
    refreshRecords,
    deleteDomain,
    toggleHomepageConfig,
    goBack,
  };
}
